import React, { useState, useCallback, useEffect } from "react";
import { Formik, Field, Form } from "formik";
import FileUploadForm from "components/PLCTABS/CompanyOfficialsTab/FileUploadForm";
import { getChangedDocumentsModuleData } from "config/changesDocumentData";
import _get from "lodash/get";
import { useDispatch, useSelector } from "react-redux";
import Button from "components/lib/Shared/Button";
import { downloadBusinessDocumentsAsync } from "state/slices/company";
import { useToast } from "hooks/useToast";
import ErrorBanner from "components/lib/Shared/ErrorBanner";
import { handleFilesUpload } from "utils/filesUtils";
import { updateCompanyOfficialAsync } from "state/slices/tables/companyOfficialsSlice";
import {
  useHistory,
  useParams,
} from "react-router-dom/cjs/react-router-dom.min";
import { updateTransaction } from "state/slices/transaction";
import { Constants } from "config/constants";
import { uploadChangeDocumentValidationSchema } from "./validationSchemas";
import { getSignedUploadValidationSchema } from "./changeDocumentSignedUnsignedValidation";

/**
 * ChangeDocumentSignedUnsigned component handles the display, upload, and download of
 * unsigned and signed change documents for company officials.
 *
 * Props:
 * - row: The data row containing the official and document info
 */
const ChangeDocumentSignedUnsigned = ({ row, setFormData, formError }) => {
  const dispatch = useDispatch();
  const [isDownloading, setIsDownloading] = useState(false);
  const { trxId } = useParams();
  const { toast } = useToast();
  const history = useHistory();
  const { transactionUnderReview } = useSelector(
    (state) => state.transactionSlice
  );
  const { transactions } = transactionUnderReview?.items;
  const [transaction, setTransaction] = useState(null);

  const search = window.location.search;
  const params = new URLSearchParams(search);
  const isChangeOfParticulars = params.get("changeOfParticulars");
  const isCessation = params.get("cessation");
  const isAppointment = params.get("appointment");

  const unsignedChangeOfParticularsDocs = {
    statutoryDeclaration:
      row?.original?.uploadChangeDocuments?.changeOfParticulars
        ?.statutoryDeclaration?.convertedPdf,
  };

  const unsignedCessationDocs = {
    letterOfResignation:
      row?.original?.uploadChangeDocuments?.cessation?.letterOfResignation
        ?.convertedPdf,
    statutoryDeclaration:
      row?.original?.uploadChangeDocuments?.cessation?.statutoryDeclaration
        ?.convertedPdf,
  };

  const unsignedAppointmentDocs = {
    consentToAct:
      row?.original?.uploadChangeDocuments?.appointment?.consentToAct
        ?.convertedPdf,
    appointmentLetter:
      row?.original?.uploadChangeDocuments?.appointment?.appointmentLetter
        ?.convertedPdf,
  };

  let unsignedFileUploads = {};
  let signedFileUploads = {};
  let documentData = [];

  const getSignedKeysFromUnsignedDocs = (unsignedDoc) => {
    return Object.fromEntries(
      Object.entries(unsignedDoc)
        .filter(([_, value]) => value) // keep only keys with values
        .map(([key]) => [key, ""])     // map to empty string
    );
  };

  const filteredDocumentData = (documentData, signedFileUploads) => {
    const validKeys = new Set(Object.keys(signedFileUploads));

    return documentData.filter((doc) =>
      validKeys.has(doc.name)
    );
  }

  if (isChangeOfParticulars) {
    unsignedFileUploads = {
      ...unsignedChangeOfParticularsDocs,
    };
    signedFileUploads = {
      statutoryDeclaration: "",
    };
    if (row?.original?.uploadChangeDocuments?.changeOfParticulars?.statutoryDeclaration?.signedPdf) {
      signedFileUploads.statutoryDeclaration = row.original.uploadChangeDocuments.changeOfParticulars.statutoryDeclaration.signedPdf;
    }
    documentData = getChangedDocumentsModuleData(
      "changeOfParticularsChangeDocuments"
    );
  } else if (isCessation) {
    unsignedFileUploads = {
      ...unsignedCessationDocs,
    };
    signedFileUploads = getSignedKeysFromUnsignedDocs(unsignedCessationDocs);
    if (row.original.uploadChangeDocuments?.cessation?.letterOfResignation?.signedPdf) {
      signedFileUploads.letterOfResignation = row.original.uploadChangeDocuments.cessation.letterOfResignation.signedPdf;
    }
    if (row.original.uploadChangeDocuments?.cessation?.statutoryDeclaration?.signedPdf) {
      signedFileUploads.statutoryDeclaration = row.original.uploadChangeDocuments.cessation.statutoryDeclaration.signedPdf;
    }
    documentData = getChangedDocumentsModuleData("cessationChangeDocuments");
    documentData = filteredDocumentData(documentData, signedFileUploads)
  } else if (isAppointment) {

    unsignedFileUploads = {
      ...unsignedAppointmentDocs,
    };
    signedFileUploads = getSignedKeysFromUnsignedDocs(unsignedAppointmentDocs);
    if (row?.original?.uploadChangeDocuments?.appointment?.consentToAct?.signedPdf) {
      signedFileUploads.consentToAct = row.original.uploadChangeDocuments.appointment.consentToAct.signedPdf;
    }
    if (row?.original?.uploadChangeDocuments?.appointment?.appointmentLetter?.signedPdf) {
      signedFileUploads.appointmentLetter = row.original.uploadChangeDocuments.appointment.appointmentLetter.signedPdf;
    }
    documentData = getChangedDocumentsModuleData("appointmentChangeDocuments");
    documentData = filteredDocumentData(documentData, signedFileUploads)
  }

  const handleDownloadDocuments = async () => {
    try {
      setIsDownloading(true);
      const businessDocuments = [];

      getChangedDocumentsModuleData("ownConsentResolutionType").map(
        (businessDocument) => {
          const fileKey = row.original.fileUploads?.[businessDocument.name];
          if (fileKey) {
            businessDocuments.push({
              key: fileKey,
              name: `${businessDocument.label}.${fileKey.split(".").pop()}`,
            });
          }
        }
      );
      await Promise.all(
        businessDocuments.map(async (businessDocument) =>
          dispatch(
            downloadBusinessDocumentsAsync({
              companyId: 1, // TODO: Replace with actual companyId if available
              businessDocument,
            })
          )
        )
      );
    } catch (error) {
      toast(
        "error",
        error?.message ?? "Something went wrong, please try again"
      );
    } finally {
      setIsDownloading(false);
    }
  };




  return (
    <div className="py-4 mt-2 flex gap-5">
      {/* Unsigned document section */}
      <div className="w-full">
        <div className="px-2">Unsigned version</div>
        <div className="p-3 mt-2 bg-white">
          <Formik
            enableReinitialize
            initialValues={{
              fileUploads: {
                ...unsignedFileUploads,
              },
            }}
            onSubmit={() => null}
          >
            {(values) => {
              return (
                <Form className="pl-4">
                  <Field
                    name="fileUploads"
                    component={FileUploadForm}
                    documentData={documentData}
                    resolvedFields={_get(document, "resolvedFields")}
                    onlyPreview
                    showDocumentLabel
                    editorKey={document.fileName}
                    documentName={"approvedChangesDocuments"}
                  />
                </Form>
              );
            }}
          </Formik>
        </div>
        <div className="mt-4">
          <Button
            className="ml-auto"
            onClick={handleDownloadDocuments}
            isLoading={isDownloading}
            loadingText="Downloading..."
          >
            Download unsigned version
          </Button>
        </div>
      </div>
      {/* Signed document upload section */}
      <div className="w-full">
        <div className="px-2">Signed version</div>
        <div className="p-3 mt-2 bg-white">
          <Formik
            initialValues={{
              fileUploads: { ...signedFileUploads },
            }}
            validationSchema={getSignedUploadValidationSchema(documentData)}
          >
            {({ values, isSubmitting, errors }) => {
              useEffect(() => {
                setFormData(values.fileUploads)
              }, [values.fileUploads])
              return (
                <Form className="pl-4">
                  <Field
                    name="fileUploads"
                    component={FileUploadForm}
                    documentData={documentData}
                    resolvedFields={_get(document, "resolvedFields")}

                    showDocumentLabel
                    editorKey={document.fileName}
                    documentName={"signedUploadChangeDocument"}
                  />
                </Form>
              );
            }}
          </Formik>
        </div>
        {formError && <ErrorBanner errors={formError} />}
      </div>
    </div>
  );
};

export default ChangeDocumentSignedUnsigned;
